import {memo} from 'react';
import type {CSSProperties} from 'react';
import Image from 'next/image';
import type {StaticImageData} from 'next/image';
import Container from '@/components/atoms/Container';
import SectionHeader from '@/components/molecules/SectionHeader';
import {useRovingTabs} from '@/hooks/useRovingTabs';
import styles from './FeatureIntegrationTabs.module.scss';

export interface FeatureItem {
    title: string;
    /** Single-colour line icon (black strokes); recoloured through a CSS mask. */
    icon: string;
}

export interface IntegrationItem extends FeatureItem {
    description: string;
}

export interface IntegrationChip {
    name: string;
    /** Omit when no logo exists; the chip then shows the name alone. */
    logo?: string | StaticImageData;
}

export interface FeatureIntegrationTabsProps {
    heading: string;
    subheading?: string;
    /** First tab: a grid of icon + title cells, with an optional chip row under it. */
    featuresLabel: string;
    features: FeatureItem[];
    chipsTitle?: string;
    chips?: IntegrationChip[];
    /** Second tab: one card per integration (icon + title + description), four to a row on large screens. */
    integrationsLabel: string;
    integrations: IntegrationItem[];
}

const CHIP_LOGO_SIZE = 22;

const iconMask = (src: string): CSSProperties => ({maskImage: `url('${src}')`, WebkitMaskImage: `url('${src}')`});

const IconTile = ({src, light = false}: {src: string; light?: boolean}) => (
    <span className={`${styles.iconTile} ${light ? styles.iconTileLight : ''}`} aria-hidden="true">
        <span className={styles.icon} style={iconMask(src)} />
    </span>
);

/**
 * Two tabs under one heading: product features as a ruled icon grid (plus a chip row of platforms), and
 * enterprise integrations as a grid of cards. Both panels are in the server HTML; the
 * inactive one is `hidden`, so switching (always user input) is the only thing that changes the height.
 */
const FeatureIntegrationTabs = ({
    heading,
    subheading,
    featuresLabel,
    features,
    chipsTitle,
    chips,
    integrationsLabel,
    integrations
}: FeatureIntegrationTabsProps) => {
    const labels = [featuresLabel, integrationsLabel];
    const {active, getTabProps, getPanelProps} = useRovingTabs(labels.length);

    return (
        <Container>
            <SectionHeader heading={heading} subheading={subheading} className={styles.header} subheadingClassName={styles.subheading} />

            <div className={styles.tabList} role="tablist" aria-label={heading}>
                {labels.map((label, index) => (
                    <button
                        key={label}
                        {...getTabProps(index)}
                        className={`font-regular-text ${styles.tab} ${index === active ? styles.tabActive : ''}`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            <div {...getPanelProps(0)}>
                <ul className={styles.featureGrid}>
                    {features.map((feature) => (
                        <li key={feature.title} className={styles.featureCell}>
                            <IconTile src={feature.icon} />
                            <h3 className={`font-headers ${styles.featureTitle}`}>{feature.title}</h3>
                        </li>
                    ))}
                </ul>

                {chips && chips.length > 0 && (
                    <div className={styles.chipsBlock}>
                        {chipsTitle && <h3 className={`font-headers ${styles.chipsTitle}`}>{chipsTitle}</h3>}
                        <ul className={styles.chips}>
                            {chips.map((chip) => (
                                <li key={chip.name} className={`font-regular-text ${styles.chip}`}>
                                    {/* Decorative: the name sits beside it. Tiny mixed SVG/WebP logos are served as-is. */}
                                    {chip.logo && (
                                        <Image
                                            src={chip.logo}
                                            alt=""
                                            width={CHIP_LOGO_SIZE}
                                            height={CHIP_LOGO_SIZE}
                                            unoptimized
                                            className={styles.chipLogo}
                                        />
                                    )}
                                    {chip.name}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            <div {...getPanelProps(1)}>
                <ul className={styles.integrationList}>
                    {integrations.map((item) => (
                        <li key={item.title} className={styles.integrationCell}>
                            <IconTile src={item.icon} light />
                            <h3 className={`font-headers ${styles.integrationTitle}`}>{item.title}</h3>
                            <p className={`font-regular-text ${styles.integrationDesc}`}>{item.description}</p>
                        </li>
                    ))}
                </ul>
            </div>
        </Container>
    );
};

export default memo(FeatureIntegrationTabs);
